export interface Rect { top: number; left: number; bottom: number; right: number; width: number; height: number }
export type Placement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'

/**
 * Where to put a floating panel next to its trigger, inside a boundary (the viewport):
 * prefer below + start-aligned; flip above when the panel does not fit below and there is more
 * room above; align to the trigger's end when it would overflow on the right (start on RTL-ish
 * overflow left); finally clamp so it never leaves the boundary. Pure, so it is unit-tested.
 */
export function computePlacement(
  trigger: Rect,
  panel: { width: number; height: number },
  boundary: { width: number; height: number },
  opts: { gap?: number; margin?: number; matchWidth?: boolean } = {}
): { top: number; left: number; width: number; maxHeight: number; placement: Placement } {
  const gap = opts.gap ?? 6
  const margin = opts.margin ?? 8
  const width = opts.matchWidth ? Math.max(trigger.width, panel.width) : panel.width
  const below = boundary.height - trigger.bottom - gap - margin
  const above = trigger.top - gap - margin
  const vertical = panel.height <= below || below >= above ? 'bottom' : 'top'
  const fitsStart = trigger.left + width <= boundary.width - margin
  const align = fitsStart ? 'start' : 'end'
  let left = align === 'start' ? trigger.left : trigger.right - width
  left = Math.max(margin, Math.min(left, boundary.width - width - margin))
  const space = vertical === 'bottom' ? below : above
  const height = Math.min(panel.height, Math.max(space, 120))
  let top = vertical === 'bottom' ? trigger.bottom + gap : trigger.top - gap - height
  top = Math.max(margin, Math.min(top, boundary.height - height - margin))
  return { top, left, width, maxHeight: Math.max(space, 120), placement: `${vertical}-${align}` as Placement }
}
